// Implements: F-UIX-010, F-UIX-009, F-UIX-014
/**
 * The only place in the application where `fetch` appears.
 *
 * One file decides credentials, headers, error translation, retries and what a
 * `401` means, and ESLint keeps it that way: `no-restricted-syntax` bans
 * `fetch` and `XMLHttpRequest` everywhere and re-enables them for this path
 * alone (design DD6, DD22).
 *
 * Four decisions, each with its reason:
 *
 *   1. `credentials: "same-origin"`, never `"include"`. Both behave the same
 *      today because the SPA and the API share one origin; `"include"` would
 *      also keep working the day somebody splits them, and that silence is
 *      exactly what must not happen.
 *   2. Every body is `application/json`. A form-encoded or multipart body
 *      towards the API is a CSRF-relevant content type, and the client has no
 *      reason to emit one (spec: client CSRF posture, point 1).
 *   3. The session cookie is never read, written or inspected here. It is
 *      `HttpOnly`; there is nothing to read and reading it would be the first
 *      step towards storing it somewhere.
 *   4. Retries: none for an `ApiError` below 500, none ever for `429` — that
 *      one is a wait the interface presents (DD8) — and two for a network
 *      failure or a 5xx, which are the two failures a second attempt can fix.
 */

import { ApiError, UNKNOWN_ERROR_CODE, toApiError } from "./ApiError";
import { API_BASE } from "./apiBase";
import { loginPathWithNext } from "./nextParam";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  readonly method?: HttpMethod | undefined;
  /** Serialised as JSON. `undefined` sends no body and no content type. */
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
  /**
   * `false` for a request that must reach the server AT MOST once: a paid or
   * otherwise non-idempotent call, where a retry after a lost response could
   * run the operation twice. Default `true`.
   */
  readonly retry?: boolean | undefined;
}

/** Attempts after the first one, for a network failure or a 5xx. */
export const MAX_RETRIES = 2;

/** The `code` of a wrong password or code a signed-in person typed (mockup v3 P1): a form error. */
export const INVALID_CREDENTIAL_CODE = "INVALID_CREDENTIAL";

/**
 * The requests whose `401` is not a session expiry.
 *
 * This is a list of named requests, not a class of them (design DD9): the
 * login POST answers `401` when the credentials are wrong, and the session
 * bootstrap answers `401` when there is simply nobody signed in. Redirecting
 * either of them would send `/login` to `/login` forever.
 *
 * An entry with a `code` is exempt only for that code: the own-account forms
 * (mockup v3 P1) answer `INVALID_CREDENTIAL` for a wrong password or code,
 * which the form says in place, while their `UNAUTHORIZED` is still a session
 * that expired under the open form and goes to `/login` like any other.
 */
const UNAUTHORIZED_EXEMPT: readonly {
  readonly method: HttpMethod;
  readonly path: string;
  readonly code?: string;
}[] = [
  { method: "POST", path: `${API_BASE}/auth/login` },
  { method: "GET", path: `${API_BASE}/auth/me/scopes` },
  { method: "POST", path: `${API_BASE}/auth/password-change`, code: INVALID_CREDENTIAL_CODE },
  { method: "POST", path: `${API_BASE}/auth/totp/enroll`, code: INVALID_CREDENTIAL_CODE },
  { method: "POST", path: `${API_BASE}/auth/totp/verify`, code: INVALID_CREDENTIAL_CODE },
  { method: "DELETE", path: `${API_BASE}/auth/totp`, code: INVALID_CREDENTIAL_CODE },
];

/** What the application does when a session turns out to be gone. */
export type UnauthorizedHandler = (redirectTo: string) => void;

/**
 * Registered by the composition root, because navigation and the query cache
 * belong to the router and to TanStack Query, and this module knows neither.
 * Until slice 3b registers one, a `401` still produces its `ApiError`; what is
 * missing is only the redirect.
 */
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Install the handler the `401` interceptor calls, or remove it with `null`.
 *
 * @returns The handler that was installed before, so a test can restore it.
 */
export function setUnauthorizedHandler(
  handler: UnauthorizedHandler | null,
): UnauthorizedHandler | null {
  const previous = unauthorizedHandler;
  unauthorizedHandler = handler;
  return previous;
}

/** The path plus query the browser is on, which is what `next` remembers. */
function currentLocation(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Same-origin absolute URL for a root-relative API path.
 *
 * The origin is written out rather than left to the fetch implementation
 * because a relative URL is not resolvable outside a document, and the tests
 * run the same code path the browser runs.
 */
function resolve(path: string): string {
  return new URL(path, window.location.origin).toString();
}

function isExempt(method: HttpMethod, path: string, code: string): boolean {
  const pathname = new URL(path, window.location.origin).pathname;
  return UNAUTHORIZED_EXEMPT.some(
    (exempt) =>
      exempt.method === method && exempt.path === pathname && (exempt.code === undefined || exempt.code === code),
  );
}

/** Decode a body that may be JSON, may be text and may be nothing at all. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (text === "") return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Not JSON: hand the raw text on, so `toApiError` can keep it as the
    // message while the code stays `UNKNOWN`.
    return text;
  }
}

function shouldRetry(attempt: number, status: number | null, allowed: boolean): boolean {
  if (!allowed) return false;
  if (attempt >= MAX_RETRIES) return false;
  // `null` is a network failure: no response arrived at all.
  return status === null || status >= 500;
}

/**
 * Perform one request through the single HTTP exit.
 *
 * @param path Root-relative API path, for example `/api/auth/sessions`.
 * @param options Method, JSON body and abort signal.
 * @returns The decoded body, or `undefined` for a `204`.
 * @throws {ApiError} For any response that is not `ok`, and for a network
 *   failure that outlives its retries.
 */
export async function httpRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T | undefined> {
  const response = await send(path, options, "application/json");
  return (await readBody(response)) as T | undefined;
}

/** A file the server answered, and the name it gave it (`null` when it gave none). */
export interface Download {
  readonly blob: Blob;
  readonly filename: string | null;
}

/** The file name of a `Content-Disposition`, reduced to its last path segment. */
function filenameOf(disposition: string | null): string | null {
  const match = /filename=(?:"([^"]*)"|([^;]*))/i.exec(disposition ?? "");
  const raw = (match?.[1] ?? match?.[2] ?? "").trim();
  const name = raw.split(/[\\/]/).pop() ?? "";
  return name === "" ? null : name;
}

/**
 * Download a file through the same exit as every other call.
 *
 * A refusal raises its `ApiError` (so a 404 is a notice, never a file that
 * holds the error body), the `401` handler and the retry of a 5xx or a network
 * failure apply as to any `GET`. The bytes are returned, not saved: saving is
 * the caller's, because it needs the DOM.
 */
export async function httpDownload(path: string, signal?: AbortSignal): Promise<Download> {
  const response = await send(path, { signal }, "*/*");
  return { blob: await response.blob(), filename: filenameOf(response.headers.get("Content-Disposition")) };
}

/** The request loop: retries, the `401` handler and the `ApiError`, answering an `ok` response. */
async function send(path: string, options: RequestOptions, accept: string): Promise<Response> {
  const method = options.method ?? "GET";
  const hasBody = options.body !== undefined;
  const retryAllowed = options.retry !== false;
  const headers: Record<string, string> = { Accept: accept };
  if (hasBody) headers["Content-Type"] = "application/json";

  const init: RequestInit = {
    method,
    headers,
    credentials: "same-origin",
    ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  };

  for (let attempt = 0; ; attempt += 1) {
    let response: Response;
    try {
      response = await fetch(resolve(path), init);
    } catch (cause) {
      if (shouldRetry(attempt, null, retryAllowed)) continue;
      throw new ApiError(0, "NETWORK", String(cause), new Headers());
    }

    if (!response.ok) {
      if (shouldRetry(attempt, response.status, retryAllowed)) continue;
      const error = toApiError(response.status, await readBody(response), response.headers);
      if (error.status === 401 && !isExempt(method, path, error.code)) {
        unauthorizedHandler?.(loginPathWithNext(currentLocation()));
      }
      throw error;
    }

    return response;
  }
}

/** Whether `cause` is the platform error of an aborted request, whichever realm made it. */
function isAbortError(cause: unknown): boolean {
  return typeof cause === "object" && cause !== null && (cause as { name?: unknown }).name === "AbortError";
}

/** What a stream call may be given. */
export interface StreamOptions {
  readonly signal?: AbortSignal | undefined;
}

/** One `data:` payload of a server-sent event block, or `null` for a block with none. */
function dataOf(block: string): string | null {
  const lines = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => (line.startsWith("data: ") ? line.slice(6) : line.slice(5)));
  return lines.length === 0 ? null : lines.join("\n");
}

function parseEvent(block: string): unknown {
  const data = dataOf(block);
  if (data === null) return undefined;
  try {
    return JSON.parse(data);
  } catch {
    // An event that is not JSON is a contract violation, not something to
    // skip: skipping would show a run that never moves to somebody waiting.
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "A stream event was not JSON", new Headers());
  }
}

/**
 * Follow a server-sent event stream and hand over each event AS IT ARRIVES.
 *
 * It lives here because `fetch` may appear in no other file, and it keeps the
 * rules of `httpRequest`: same-origin credentials, the `ApiError` of a refusal
 * and the `401` handler. It differs on purpose in one place: it NEVER retries.
 * A retried stream would replay events the caller has already acted on.
 *
 * The body is decoded incrementally and split on the blank line that closes an
 * event, so an event cut across two network chunks is reassembled and the last
 * one is delivered even when the server closes without the final blank line.
 * Only `data:` lines count; comments and other fields are ignored.
 *
 * @throws {ApiError} For a refusal, a network failure or an event that is not
 *   JSON. An abort surfaces as the platform `AbortError`, untouched, so the
 *   caller can tell "I stopped it" from "it failed".
 */
export async function* httpStream<T>(
  path: string,
  options: StreamOptions = {},
): AsyncGenerator<T, void, undefined> {
  const init: RequestInit = {
    method: "GET",
    headers: { Accept: "text/event-stream" },
    credentials: "same-origin",
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  };

  let response: Response;
  try {
    response = await fetch(resolve(path), init);
  } catch (cause) {
    // By name, not `instanceof DOMException`: the platform may raise it from
    // another realm (a worker, a polyfill, the test environment).
    if (isAbortError(cause)) throw cause;
    throw new ApiError(0, "NETWORK", String(cause), new Headers());
  }

  if (!response.ok) {
    const error = toApiError(response.status, await readBody(response), response.headers);
    if (error.status === 401 && !isExempt("GET", path, error.code)) {
      unauthorizedHandler?.(loginPathWithNext(currentLocation()));
    }
    throw error;
  }
  if (response.body === null) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // A real browser errors the body when the request is aborted; a mocked or
  // proxied body may not, and a read that waits forever would leak the loop.
  // Racing the read against the signal makes an abort end it everywhere.
  const abortion = abortionOf(options.signal);
  try {
    for (;;) {
      const { done, value } = await Promise.race([reader.read(), abortion.promise]);
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      const blocks = buffer.replaceAll("\r\n", "\n").split("\n\n");
      // The last piece is an event still being received, unless the stream ended.
      buffer = done ? "" : (blocks.pop() ?? "");
      for (const block of blocks) {
        const event = parseEvent(block);
        if (event !== undefined) yield event as T;
      }
      if (done) return;
    }
  } finally {
    abortion.dispose();
    // Not awaited: cancelling waits for the source, and a source that is itself
    // blocked must not hold the caller after the caller has already stopped.
    void reader.cancel().catch(() => undefined);
  }
}

/** A promise that rejects with an `AbortError` when `signal` aborts, and a way to stop listening. */
function abortionOf(signal: AbortSignal | undefined): {
  readonly promise: Promise<never>;
  readonly dispose: () => void;
} {
  if (signal === undefined) return { promise: new Promise<never>(() => undefined), dispose: () => undefined };
  let onAbort: () => void = () => undefined;
  const promise = new Promise<never>((_resolve, reject) => {
    onAbort = () => {
      reject(new DOMException("The stream was aborted.", "AbortError"));
    };
    if (signal.aborted) onAbort();
    else signal.addEventListener("abort", onAbort, { once: true });
  });
  // Nobody may be racing it when it fires (the loop can be between two reads).
  promise.catch(() => undefined);
  return {
    promise,
    dispose: () => {
      signal.removeEventListener("abort", onAbort);
    },
  };
}
